import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    Card,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    Radio,
    RadioGroup
} from "@mui/material";
import Grid from '@mui/material/Grid2'; // Using GridV2
import { useTheme } from "@mui/material/styles";
import { DateTimePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from 'dayjs';
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { useAuth } from '../../../context/AuthContext';
import { useSnackbar } from '../../../context/SnackbarContext';
import { fetchAddressSuggestionsAuth, fetchPublicEventDetail, NewEventListingAuth, UpdateEventListingAuth } from '../../../services/eventServices';
import { theme } from "../../../theme";
import { createInputSX } from "../../../utils/constants";
import { isValidURL } from '../../../utils/helperUtils';
import CreateEditFormComponent from '../../components/CreateEditFormComponent';
import LoadingComponent from '../../components/LoadingComponent';
import EventMapComponent from '../components/EventMapComponent';


const steps = [
    "🎊 Let’s Kick This Off!",
    "🕰 Tick-Tock, Pick a Slot!",
    "👀 Extra Deets!",
    "📸 Proof It’s Real – Upload Pics!",
    "📋 Permits and Stuff",
    "🎟 Ticket to Ride – Set Your Price!",
    "🎯 Final Check – All Set?"
];

const socialMediaOptions = [
    { label: "Instagram", value: "instagram" },
    { label: "Twitter", value: "twitter" },
    { label: "TikTok", value: "tiktok" }
];

// Reusable SummaryAccordion component
function SummaryAccordion({ title, children, defaultExpanded = false }) {
    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            square={true}
            sx={accordionSX}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon className='manage-card-icon-circle' sx={{ color: theme.colors.events.primary }} />}>
                <Typography sx={{ fontWeight: "bold" }}>{title}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                {children}
            </AccordionDetails>
        </Accordion>
    );
}

// Constant for Accordion sx styling
const accordionSX = {
    borderRadius: 3,
    mb: 2,
    "&:before": { display: "none" },
    overflow: "hidden"
};

// Helper: Format address
function formatAddress(address) {
    if (!address) return "";
    return [
        address.line1,
        address.line2,
        address.city,
        address.state,
        address.zip_code,
    ].filter(Boolean).join(", ");
}

// Helper: Render Event Map section
function renderMapSection(formData) {
    if (formData.location && formData.location.coordinates.length === 2) {
        return (
            <EventMapComponent
                coordinates={{
                    lat: formData.location.coordinates[1],
                    lng: formData.location.coordinates[0]
                }}
                height={{ xs: 300, sm: 350 }}
            />
        );
    } else {
        return (
            <Box>
                <Typography variant="body2" color="text.secondary" display="inline"
                >
                    Type and select an address to see it on the map
                </Typography>
            </Box>
        );
    }
}

// Commented Social Media handlers (for future use)
/*
const handleAddSocialMedia = () => {
    if (formData.socialMediaLinks.length < 3) {
        setFormData({
            ...formData,
            socialMediaLinks: [...formData.socialMediaLinks, { platform: "", username: "" }]
        });
    }
};

const handleChangeSocialMedia = (index, field, value) => {
    const updatedLinks = [...formData.socialMediaLinks];
    updatedLinks[index][field] = value;
    setFormData({ ...formData, socialMediaLinks: updatedLinks });
};

const handleRemoveSocialMedia = (index) => {
    const updatedLinks = formData.socialMediaLinks.filter((_, i) => i !== index);
    setFormData({ ...formData, socialMediaLinks: updatedLinks });
};
*/

export default function CreateEditEventsView() {
    const themeX = useTheme();
    const { isAuthenticated, login, logout } = useAuth();
    const { slug } = useParams();
    const showSnackbar = useSnackbar();
    const navigate = useNavigate();

    const maxSteps = steps.length;

    const [activeStep, setActiveStep] = useState(0);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        short_description: "",
        description: "",
        website: "",
        status: "draft",
        start_time: null,
        end_time: null,
        application_deadline: null,
        banner_photo: null,
        tnc: "",
        permit_required: false,
        permit_description: "",
        booth_types: [{
            "name": "10x10",
            "description": "Small booth",
            "quantity_available": 5,
            "cost": 100
        }],
        address: {
            line1: "",
            line2: "",
            city: "",
            state: "",
            country: "",
            zip_code: ""
        },
        location: {
            type: "Point",
            coordinates: []
        },
        payment_mode: "stripe",
    });
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [addressOptions, setAddressOptions] = useState([]);
    const [locationInput, setLocationInput] = useState("");

    useEffect(() => {
        const fetchEventData = async () => {
            if (!slug) return;

            setLoading(true);
            try {
                const response = await fetchPublicEventDetail(slug, login, logout);
                if (response.success && response.data) {
                    const data = response.data;

                    setFormData(prev => ({
                        ...prev,
                        name: data.name || "",
                        short_description: data.short_description || "",
                        description: data.description || "",
                        website: data.website || "",
                        status: data.status || "draft",
                        start_time: data.start_time || null,
                        end_time: data.end_time || null,
                        application_deadline: data.application_deadline || null,
                        banner_photo: data.banner_photo || null, // File objects cannot be prefilled
                        booth_types: data.booth_types || [],
                        address: data.address || {
                            line1: "",
                            line2: "",
                            city: "",
                            state: "",
                            country: "",
                            zip_code: ""
                        },
                        location: data.location || {
                            type: "Point",
                            coordinates: []
                        },
                        permit_required: data.permit_required || false,
                        permit_description: data.permit_description || "",
                        payment_mode: data.payment_mode || "stripe"
                    }));
                }
            } catch (err) {
                console.error("Failed to load event data:", err);
                setError(err.message || "Something went wrong.");
            } finally {
                setLoading(false);
            }
        };

        fetchEventData();
    }, [slug]);

    const validateStep = () => {
        switch (activeStep) {
            case 0: // Step 1: Event Details
                return (
                    formData.name.trim() !== "" &&
                    formData.short_description.trim() !== ""
                    && formData.address.zip_code.trim() !== ""
                );
            case 1: // Step 2: Date & Time
                return formData.start_time !== null &&
                    formData.end_time !== null &&
                    formData.application_deadline !== null;
            case 2:
                if (formData.payment_mode === "cash") {
                    return formData.website.trim().length > 0 && isValidURL(formData.website);
                }
                return formData.website.length === 0 || isValidURL(formData.website);
            case 3: // Step 4: Photo Upload (No required fields)
                return true;
            case 4: // Step 5: Permits & T&Cs (No required fields)
                return true;
            case 5: // Step 6: Pricing & Slots (At least 1 pricing entry required)
                return formData.booth_types.length === 0 || formData.booth_types.length > 0 &&
                    formData.booth_types.every(
                        (booth) => booth.name.trim() !== "" && booth.quantity_available !== ""
                        // && booth.cost !== ""
                    );
            case 6: // Step 7: Summary (Always Valid)
                return true;
            default:
                return false;
        }
    };

    const handleAddSocialMedia = () => {
        if (formData.socialMediaLinks.length < 3) {
            setFormData({
                ...formData,
                socialMediaLinks: [...formData.socialMediaLinks, { platform: "", username: "" }]
            });
        }
    };

    const handleChangeSocialMedia = (index, field, value) => {
        const updatedLinks = [...formData.socialMediaLinks];
        updatedLinks[index][field] = value;
        setFormData({ ...formData, socialMediaLinks: updatedLinks });
    };

    const handleRemoveSocialMedia = (index) => {
        const updatedLinks = formData.socialMediaLinks.filter((_, i) => i !== index);
        setFormData({ ...formData, socialMediaLinks: updatedLinks });
    };

    const handleAddPricing = () => {
        if (formData.booth_types.length < 3) {
            setFormData({
                ...formData,
                booth_types: [
                    ...formData.booth_types,
                    {
                        name: "",
                        description: "",
                        quantity_available: "",
                        cost: ""
                    }
                ]
            });
        }
    };

    const handleChangePricing = (index, field, value) => {
        const updatedBooths = [...formData.booth_types];
        updatedBooths[index][field] = value;
        setFormData({ ...formData, booth_types: updatedBooths });
    };

    const handleRemovePricing = (index) => {
        const updatedBooths = formData.booth_types.filter((_, i) => i !== index);
        setFormData({ ...formData, booth_types: updatedBooths });
    };

    const handleAddressInputChange = async (query) => {
        if (query.length < 3) {
            return;
        }

        const res = await fetchAddressSuggestionsAuth(query, login, logout);
        if (res.success) {
            setAddressOptions(res.data);
        }
    };

    const handleBannerUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setFormData({ ...formData, banner_photo: file });
        }
    };

    const handleEventPhotoUpload = (event) => {
        const files = Array.from(event.target.files);
        if (formData.eventPhotos.length + files.length <= 6) {
            const newPhotos = files.map((file) => URL.createObjectURL(file));
            console.log({ newPhotos })
            setFormData({ ...formData, eventPhotos: [...formData.eventPhotos, ...newPhotos] });
        }
    };

    const handleRemovePhoto = (type, index) => {
        if (type === "banner") {
            setFormData({ ...formData, banner_photo: null });
        } else {
            const updatedPhotos = formData.eventPhotos.filter((_, i) => i !== index);
            setFormData({ ...formData, eventPhotos: updatedPhotos });
        }
    };

    const handlePreviewPhoto = (index, type) => {
        let images = [];
        if (type === "banner") {
            images = [{ src: URL.createObjectURL(formData.banner_photo) }];
        } else {
            images = formData.eventPhotos.map((photo) => ({ src: photo }));
        }

        setLightboxImages(images);
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    // Handle input changes
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleDateChange = (field, value) => {
        setFormData((prev) => {
            const updated = { ...prev, [field]: value ? value.toISOString() : null };

            if (field === "start_time") {
                if (prev.end_time) {
                    const newStart = dayjs(value);
                    const oldEnd = dayjs(prev.end_time);

                    // If new start date is different than end date
                    if (!newStart.isSame(oldEnd, "day")) {
                        updated.end_time = oldEnd
                            .year(newStart.year())
                            .month(newStart.month())
                            .date(newStart.date())
                            .toISOString();
                    }

                    // If new start is after end, set end_time = start_time + 1 hour
                    if (newStart.isAfter(dayjs(updated.end_time))) {
                        updated.end_time = newStart.add(1, "hour").toISOString();
                    }
                }
            }

            if (field === "end_time" && prev.start_time) {
                const selectedEnd = dayjs(value);
                const startTime = dayjs(prev.start_time);

                const merged = startTime
                    .hour(selectedEnd.hour())
                    .minute(selectedEnd.minute())
                    .second(selectedEnd.second());

                // Force end_time to be after start_time
                if (merged.isBefore(startTime)) {
                    updated.end_time = startTime.add(1, "hour").toISOString();
                } else {
                    updated.end_time = merged.toISOString();
                }
            }

            return updated;
        });
    };

    // Navigation
    const handleNext = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    // Submit form
    const handleSubmit = async () => {
        console.log("Form Data Submitted:", formData);
        setLoading(true);

        try {
            const response = await NewEventListingAuth(formData, login, logout);
            if (response.success) {
                console.log("Event created successfully:", response.data);

                showSnackbar("🎉 Event creation successful!", "success");
                navigate("/_/events/manage", { replace: true });

            }
        } catch (error) {
            console.error("Submission error:", error);

            setError(`${error.message}` || "Something went wrong.");
        }

        finally {
            setTimeout(() => {
                setLoading(false);
            }, 3000);
        }
    };

    // Update event handler
    const handleUpdate = async () => {
        if (!slug) {
            setError("No event slug provided for update.");
            return;
        }

        setLoading(true);
        try {
            // Exclude banner_photo if it's a URL (not a File)
            let updateData = { ...formData };
            if (
                updateData.banner_photo &&
                typeof updateData.banner_photo === 'string' &&
                updateData.banner_photo.startsWith('http')
            ) {
                const { banner_photo, ...rest } = updateData;
                updateData = rest;
            }

            const response = await UpdateEventListingAuth(slug, updateData, login, logout);
            if (response.success) {
                showSnackbar("✅ Event updated successfully!", "success");
                navigate("/_/events/manage", { replace: true });
            }
        } catch (error) {
            console.error("Update error:", error);
            setError(`${error.message}` || "Something went wrong.");
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 3000);
        }
    };

    if (loading) {
        return (
            <LoadingComponent />
        );
    }
    return (
        <>
            <CreateEditFormComponent
                bgColor={theme.colors.events.secondary}
                title={slug ? "Update Event Info ✏️" : "Create a New Event 🚀"}
                titleColor={theme.colors.events.primaryDark}
                error={error}
                setError={setError}
                formBgColor={theme.colors.events.primary}
                maxSteps={maxSteps}
                activeStep={activeStep}
                validateStep={validateStep}
                handleBack={handleBack}
                handleNext={handleNext}
                handleSubmit={handleSubmit}
                handleUpdate={handleUpdate}
                slug={slug}
            >
                {/* Stepper Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: 'center',
                        pt: 2,
                        pb: 1
                    }}
                >
                    <Typography variant="h6" sx={{ color: theme.colors.events.secondary, fontWeight: 'bold' }} gutterBottom>{steps[activeStep]}</Typography>
                </Box>

                {/* Step Content */}
                <Box sx={{ paddingX: 3, pb: 2 }}>
                    {/* STEP 1: Event Details */}
                    {activeStep === 0 && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Event Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    variant="standard"
                                    sx={createInputSX}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Event Summary"
                                    name="short_description"
                                    multiline
                                    maxRows={3}
                                    slotProps={{ htmlInput: { maxLength: 100 } }}
                                    helperText={`${formData.short_description.length}/${100}`}
                                    value={formData.short_description}
                                    onChange={handleChange}
                                    variant="standard"
                                    sx={createInputSX}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    fullWidth
                                    freeSolo
                                    options={addressOptions.map((option) => option.full_address)}
                                    inputValue={locationInput || formatAddress(formData.address)}
                                    onInputChange={(event, newValue, reason) => {
                                        if (reason === 'clear') {
                                            setLocationInput('');
                                            handleAddressInputChange('');
                                            setFormData({
                                                ...formData,
                                                address: {
                                                    line1: "",
                                                    line2: "",
                                                    city: "",
                                                    state: "",
                                                    country: "",
                                                    zip_code: ""
                                                },
                                                location: {
                                                    type: "Point",
                                                    coordinates: []
                                                }
                                            });
                                        }
                                        else {
                                            setLocationInput(newValue);
                                            handleAddressInputChange(newValue);
                                        }
                                    }}
                                    onChange={(event, newValue) => {
                                        const selected = addressOptions.find(option => option.full_address === newValue);
                                        if (selected) {
                                            const { address, location } = selected;
                                            setFormData({
                                                ...formData,
                                                address: address,
                                                location: {
                                                    type: "Point",
                                                    coordinates: [location.longitude, location.latitude]
                                                }
                                            });
                                            setLocationInput(selected.full_address);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            required
                                            label="Event Location"
                                            variant="standard"
                                            sx={createInputSX}
                                        />
                                    )}
                                    clearOnEscape
                                    clearText="Clear"
                                    disableClearable={false}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }} container direction={'column'}>
                                {renderMapSection(formData)}
                            </Grid>
                        </Grid>
                    )}

                    {/* STEP 2: Date & Time */}
                    {activeStep === 1 && (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Grid container spacing={2} sx={{ mt: 2 }}>
                                <Grid size={{ xs: 12, lg: 6 }}>
                                    <DateTimePicker
                                        label="Event Date & Start Time"
                                        format="ddd, D MMMM, h:mm A"
                                        value={formData.start_time ? dayjs(formData.start_time) : null}
                                        onChange={(value) => handleDateChange("start_time", value)}
                                        sx={createInputSX}
                                        slotProps={{
                                            textField: {
                                                variant: "standard",
                                                // error: !formData.start_time,
                                                helperText: !formData.start_time ? "Date & Time is required" : "",
                                            },
                                        }}
                                        disablePast={!slug}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, lg: 6 }}>
                                    <TimePicker
                                        label="Event End Time"
                                        disabled={!formData.start_time}
                                        value={formData.end_time ? dayjs(formData.end_time) : null}
                                        onChange={(value) => handleDateChange("end_time", value)}
                                        sx={createInputSX}
                                        slotProps={{
                                            textField: { variant: "standard", required: true },
                                        }}
                                        minutesStep={5}
                                        shouldDisableTime={(timeValue, clockType) => {
                                            if (clockType === 'hours' && formData.start_time) {
                                                const startHour = dayjs(formData.start_time).hour();
                                                return timeValue < startHour;
                                            }
                                            if (clockType === 'minutes' && formData.start_time && formData.end_time) {
                                                const start = dayjs(formData.start_time);
                                                const end = dayjs(formData.end_time);
                                                if (start.hour() === end.hour()) {
                                                    const startMinute = start.minute();
                                                    return timeValue < startMinute;
                                                }
                                            }
                                            return false;
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, lg: 6 }} md={6}>
                                    <DateTimePicker
                                        label="Application Deadline"
                                        format="ddd, D MMMM, h:mm A"
                                        value={formData.application_deadline ? dayjs(formData.application_deadline) : null}
                                        onChange={(value) => handleDateChange("application_deadline", value)}
                                        sx={createInputSX}
                                        slotProps={{
                                            textField: {
                                                variant: "standard",
                                                // error: !formData.application_deadline,
                                                helperText: !formData.application_deadline ? "Date & Time is required" : "",
                                            },
                                        }}
                                        disablePast={!slug}
                                    />
                                </Grid>
                            </Grid>
                        </LocalizationProvider>
                    )}

                    {/* STEP 3: Organizer Info */}
                    {activeStep === 2 && (
                        <Grid container spacing={2}>
                            {/* Payment Mode Radio */}
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="subtitle1" sx={{ color: theme.colors.events.secondary}}>
                                    Payment Mode
                                </Typography>
                                <RadioGroup
                                    row
                                    name="payment_mode"
                                    value={formData.payment_mode}
                                    onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                                >
                                    <FormControlLabel
                                        value="stripe"
                                        control={<Radio sx={{ color: theme.colors.events.secondary, '&.Mui-checked': { color: theme.colors.events.secondary } }} />}
                                        label="Online/Stripe"
                                        sx={{ color: theme.colors.events.secondary }}
                                    />
                                    <FormControlLabel
                                        value="cash"
                                        control={<Radio sx={{ color: theme.colors.events.secondary, '&.Mui-checked': { color: theme.colors.events.secondary } }} />}
                                        label="Cash"
                                        sx={{ color: theme.colors.events.secondary }}
                                    />
                                </RadioGroup>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label={formData.payment_mode === 'cash' ? 'Event Apply Link' : 'Event Website (Optional)'}
                                    name={'website'}
                                    value={formData['website']}
                                    onChange={handleChange}
                                    variant="standard"
                                    placeholder='https://sidewa.lk'
                                    sx={createInputSX}
                                    error={formData['website'].length > 0 && !isValidURL(formData['website'])}
                                    helperText={(!isValidURL(formData['website']) && formData['website'].length > 0) ? 'Invalid URL' : ''}
                                    required={formData.payment_mode === 'cash'}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label={'Event Description'}
                                    name={'description'}
                                    value={formData['description']}
                                    onChange={handleChange}
                                    variant="standard"
                                    sx={createInputSX}
                                    multiline={true}
                                />
                            </Grid>
                            {/* Social Media Inputs */}
                            {/* {formData.socialMediaLinks.map((social, index) => (
                                <Grid container key={index} spacing={2} sx={{ mt: 1 }}>
                                    <Grid size={{ xs: 6, lg: 4 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Platform"
                                            value={social.platform}
                                            onChange={(e) => handleChangeSocialMedia(index, "platform", e.target.value)}
                                            variant="standard"
                                            sx={createInputSX}
                                        >
                                            {socialMediaOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value} color="primary"
                                                    sx={{ color: theme.colors.events.primaryDark }} >
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid size={{ xs: 6, lg: 4 }}>
                                        <TextField
                                            fullWidth
                                            label="Username"
                                            value={social.username}
                                            onChange={(e) => handleChangeSocialMedia(index, "username", e.target.value)}
                                            variant="standard"
                                            sx={createInputSX}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, lg: 2 }} display="flex" alignItems="center">
                                        <Button onClick={() => handleRemoveSocialMedia(index)} sx={{ color: theme.colors.events.primaryDark }} startIcon={<CloseIcon />}>
                                            Remove
                                        </Button>
                                    </Grid>
                                </Grid>
                            ))} */}

                            {/* Add Social Media Button */}
                            {/* {formData.socialMediaLinks.length < 3 && (
                                <Grid size={{ xs: 12 }}>
                                    <Button onClick={handleAddSocialMedia} variant="outlined" color="secondary" component="label" sx={{ borderRadius: 4 }} size="small">
                                        + Add Social Media
                                    </Button>
                                </Grid>
                            )} */}
                        </Grid>
                    )}

                    {/* STEP 4: Photo Upload */}
                    {activeStep === 3 && (
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            {/* Banner Photo Section */}
                            <Grid size={{ xs: 12 }}>
                                <Box >
                                    <Typography color={theme.colors.events.secondary}>
                                        Banner Photo (1 Max)
                                    </Typography>
                                    <Divider sx={{ bgcolor: theme.colors.events.secondary }} />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                {formData.banner_photo ? (
                                    <Card sx={{ position: "relative", height: 100, display: "inline-block", my: 1 }}>
                                        <img
                                            src={slug ? formData.banner_photo : URL.createObjectURL(formData.banner_photo)}
                                            alt="Banner"
                                            style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 8 }}
                                            onClick={() => handlePreviewPhoto(0, "banner")}
                                        />
                                        <IconButton
                                            sx={{ position: "absolute", top: 0, right: 0, background: "rgba(255, 255, 255, 0.6)" }}
                                            onClick={() => handleRemovePhoto("banner")}
                                        >
                                            <CloseIcon sx={{ color: theme.colors.events.primaryDark }} />
                                        </IconButton>
                                    </Card>
                                ) : (
                                    <Button variant="outlined" color="secondary" component="label" sx={{ borderRadius: 4 }} size="small">
                                        + Upload Banner
                                        <input type="file" hidden accept=".jpg, .jpeg, .png" onChange={handleBannerUpload} />
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    )}

                    {/* STEP 5: Permits & T&Cs */}
                    {activeStep === 4 && (
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.permit_required}
                                            onChange={e => setFormData({ ...formData, permit_required: e.target.checked })}
                                            name="permit_required"
                                            sx={{
                                                color: theme.colors.events.secondary,
                                                '&.Mui-checked': {
                                                    color: theme.colors.events.secondary
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: theme.colors.events.secondary }}>
                                            Permits required for this event?
                                        </Typography>
                                    }
                                />
                            </Grid>
                            {formData.permit_required && (
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        label="Permit requirements (Optional)"
                                        name="permit_description"
                                        value={formData.permit_description}
                                        onChange={handleChange}
                                        variant="standard"
                                        sx={createInputSX}
                                        multiline
                                    />
                                </Grid>
                            )}
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Terms & Conditions (Optional)"
                                    name="tnc"
                                    value={formData.tnc}
                                    onChange={handleChange}
                                    variant="standard"
                                    sx={createInputSX}
                                    multiline
                                />
                            </Grid>
                        </Grid>
                    )}

                    {/* STEP 6: Pricing & Slots */}
                    {activeStep === 5 && (
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            {formData?.booth_types.length > 0 && formData.booth_types.map((booth, index) => (
                                <Grid container key={index} spacing={2} sx={{ mt: 1 }}>
                                    {[
                                        { label: "Booth Size", field: "name", required: true },
                                        { label: "Number of Slots", field: "quantity_available", type: "number", required: true },
                                        { label: "Cost", field: "cost", type: "number", required: true, adornment: "$" },
                                        { label: "Description", field: "description" }
                                    ].map(({ label, field, type = "text", required = false, adornment }) => (
                                        <Grid size={{ xs: 12, lg: 3 }} key={`${index}-${field}`}>
                                            <TextField
                                                fullWidth
                                                required={required}
                                                label={label}
                                                type={type}
                                                value={booth[field]}
                                                onChange={(e) => handleChangePricing(index, field, e.target.value)}
                                                variant="standard"
                                                sx={createInputSX}
                                                InputProps={
                                                    adornment
                                                        ? {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    {adornment}
                                                                </InputAdornment>
                                                            )
                                                        }
                                                        : undefined
                                                }
                                            />
                                        </Grid>
                                    ))}
                                    <Grid size={{ xs: 12, lg: 2 }} display="flex" alignItems="center">
                                        <Button onClick={() => handleRemovePricing(index)} sx={{ color: theme.colors.events.primaryDark }} startIcon={<CloseIcon />}>
                                            Remove
                                        </Button>
                                    </Grid>
                                </Grid>
                            ))}

                            {/* Add Pricing Entry Button */}
                            {formData.booth_types.length < 3 && (
                                <Grid size={{ xs: 12 }}>
                                    <Button onClick={handleAddPricing} variant="outlined" color="secondary" component="label" sx={{ borderRadius: 4 }} size="small">
                                        + Add Pricing Option
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    )}

                    {/* STEP 7: Summary */}
                    {activeStep === 6 && (
                        <Box sx={{ mt: 1 }}>
                            {/* Event Details */}
                            <SummaryAccordion title="🎊 Event Details" defaultExpanded>
                                <Typography><b>Event Name:</b> {formData.name}</Typography>
                                {/* <Typography><b>Event Handle:</b> {formData.eventSlug || "N/A"}</Typography> */}
                                <Typography><b>Summary:</b> {formData?.short_description}</Typography>
                                <Typography>
                                    <b>Address:</b> {formatAddress(formData.address)}
                                </Typography>
                            </SummaryAccordion>

                            {/* Date & Time */}
                            <SummaryAccordion title="🕰 Date & Time">
                                <Typography><b>Start Time:</b> {formData?.start_time ? dayjs(formData.start_time).format("ddd, D MMMM, h:mm A") : "N/A"}</Typography>
                                <Typography><b>End Time:</b> {formData?.end_time ? dayjs(formData.end_time).format("ddd, D MMMM, h:mm A") : "N/A"}</Typography>
                                <Typography><b>Application Deadline:</b> {formData?.application_deadline ? dayjs(formData.application_deadline).format("ddd, D MMMM, h:mm A") : "N/A"}</Typography>
                            </SummaryAccordion>

                            {/* Social Media / Additional Info */}
                            <SummaryAccordion title="👀 Additional Info">
                                {
                                    formData.website ? (<>
                                        <Typography><b>Website: </b>{formData.website}</Typography></>) : (
                                        <Typography>No link added. 💔</Typography>
                                    )
                                }
                                <br/>
                                {
                                    formData.description ? (
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: theme.colors.events.primaryDark,
                                                whiteSpace: "pre-line",
                                                textAlign: 'left',
                                                lineHeight: 1.6,
                                            }}><b>Description:</b> {formData?.description}</Typography>) : (
                                        <Typography>No description added. 🌪️</Typography>
                                    )
                                }
                            </SummaryAccordion>

                            {/* Photos */}
                            <SummaryAccordion title="📸 Uploaded Photos">
                                {formData?.banner_photo ? (
                                    <Typography><b>Banner Photo:</b> Uploaded ✅</Typography>
                                ) : (
                                    <Typography>No banner photo uploaded. 😭</Typography>
                                )}
                            </SummaryAccordion>

                            {/* Permits & T&Cs */}
                            <SummaryAccordion title="📋 Permits and T&Cs">
                                <Typography>
                                    <b>Permits required:</b> {formData.permit_required ? "Yes" : "No"}
                                </Typography>
                                {formData.permit_required && (
                                    <Typography>
                                        <b>Permit Details:</b> {formData.permit_description || "N/A"}
                                    </Typography>
                                )}
                                <Typography variant="body1"
                                    sx={{
                                        color: theme.colors.events.primaryDark,
                                        whiteSpace: "pre-line",
                                        textAlign: 'left',
                                        lineHeight: 1.6,
                                    }}>
                                    <b>Terms & Conditions:</b> {formData.tnc ? formData.tnc : "None"}
                                </Typography>
                            </SummaryAccordion>
                            {/* Pricing & Slots */}
                            <SummaryAccordion title="🎟 Pricing & Slots">
                                {formData?.booth_types.length > 0 ? (
                                    formData?.booth_types.map((booth, index) => (
                                        <React.Fragment key={index}>
                                            {index > 0 && <Divider sx={{ my: 1 }} />}
                                            <Typography>
                                                <b>Size:</b> {booth.name}<br />
                                                <b>Description:</b> {booth.description}<br />
                                                <b>Quantity:</b> {booth.quantity_available}, <b>Cost:</b> ${booth.cost} 💸
                                            </Typography>
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <Typography>No booth types added. This event is Free. 🫶🏼</Typography>
                                )}
                            </SummaryAccordion>
                        </Box>
                    )}
                </Box>
            </CreateEditFormComponent>
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={lightboxImages}
                index={lightboxIndex}
            />
        </>
    );
}